import { useRef, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ChevronDown, Check } from "lucide-react";

const VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_8465ce77-9585-4fff-bcf3-0cae1d417cf9/artifacts/m6m5eyq4_0513%281%29.mp4";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Hero() {
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [brand, setBrand] = useState("");
  const [fuel, setFuel] = useState("");
  const [type, setType] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/vehicles`)
      .then((r) => setVehicles(r.data || []))
      .catch(() => {});
  }, []);

  // Standard car brands list — merged with DB-derived brands so the dropdown
  // always shows the full market plus any custom brand seeded by admin.
  const STANDARD_BRANDS = useMemo(
    () => [
      "Abarth", "Active", "Aixam", "Alfa Romeo", "Audi", "BMW", "BYD", "Chevrolet",
      "Chrysler", "Citroën", "Cupra", "Dacia", "Daihatsu", "Dodge", "DR", "DS",
      "Fiat", "Ford", "Honda", "Hyundai", "Infiniti", "Isuzu", "Iveco", "Jaguar",
      "Jeep", "Kia", "Lancia", "Land Rover", "Lexus", "Ligier", "Lotus", "Maserati",
      "Mazda", "Mercedes-Benz", "MG", "Microcar", "Mini", "Mitsubishi", "Nissan",
      "Opel", "Peugeot", "Polestar", "Porsche", "Renault", "SEAT", "Škoda", "Smart",
      "SsangYong", "Subaru", "Suzuki", "Tesla", "Toyota", "Volkswagen", "Volvo",
    ],
    []
  );

  // Distinct values from inventory, merged with standard list (deduped, sorted)
  const brands = useMemo(
    () => Array.from(new Set([...STANDARD_BRANDS, ...vehicles.map((v) => v.brand)])).sort(),
    [vehicles, STANDARD_BRANDS]
  );
  const fuels = useMemo(
    () => Array.from(new Set(vehicles.map((v) => v.fuel))).sort(),
    [vehicles]
  );

  // Match a vehicle against the selected "Tipologia" key.
  // Supports both new schema (type=auto + condition) and legacy schema.
  const matchType = (v, key) => {
    if (!key) return true;
    const t = (v.type || "").toLowerCase();
    const c = (v.condition || "").toLowerCase();
    if (key === "scooter") return t === "scooter" || t === "scooter_cabinato";
    if (key === "moto") return t === "moto";
    if (key === "auto_usata") return (t === "auto" && c === "usato") || t === "auto_usata";
    if (key === "auto_nuova") return (t === "auto" && c === "nuovo") || t === "auto_nuova";
    return t === key;
  };

  // Live count of matches
  const matchCount = useMemo(() => {
    return vehicles.filter((v) => {
      if (brand && v.brand !== brand) return false;
      if (fuel && v.fuel !== fuel) return false;
      if (!matchType(v, type)) return false;
      return true;
    }).length;
  }, [vehicles, brand, fuel, type]);

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (brand) params.set("brand", brand);
    if (fuel) params.set("fuel", fuel);
    if (type) params.set("type", type);
    navigate(`/veicoli?${params.toString()}#vetrina`);
  };

  const scrollToVetrina = () => {
    const el = document.getElementById("vetrina");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else navigate("/veicoli");
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[760px] md:min-h-[860px] w-full overflow-hidden bg-[#0a0a0a]"
    >
      <video
        ref={videoRef}
        data-testid="hero-video"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
          videoLoaded ? "opacity-100" : "opacity-0"
        }`}
        src={VIDEO_URL}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        onLoadedData={() => setVideoLoaded(true)}
      />
      {!videoLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-200 via-zinc-300 to-zinc-100" />
      )}

      {/* Overlay for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/25 to-black/55" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 30% 70%, rgba(124,252,0,0.12), transparent 55%)",
        }}
      />

      {/* Content */}
      <div className="relative pt-32 md:pt-36 pb-12 max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-on-dark text-[11px] uppercase tracking-[0.25em] text-white mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7CFC00] animate-pulse" />
            Vetrina aperta · Zibido S. Giacomo (MI)
          </div>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05] text-white drop-shadow-lg">
            La tua nuova{" "}
            <span className="italic font-normal text-[#7CFC00]">mobilità</span>,
            <br className="hidden md:block" /> a due e quattro ruote.
          </h1>
          <p className="mt-5 text-base md:text-lg text-zinc-100 max-w-2xl mx-auto leading-relaxed">
            Scooter cabinati di ultima generazione e auto usate selezionate.
            Innovazione, sicurezza e un'esperienza d'acquisto trasparente.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={scrollToVetrina}
              data-testid="hero-cta-vedi-veicoli"
              className="px-7 py-3.5 rounded-full bg-[#5ab500] text-white text-sm font-bold tracking-wide hover:bg-[#4a9a00] hover:shadow-[0_8px_28px_rgba(90,181,0,0.5)] transition-all"
            >
              Vedi i Veicoli
            </button>
            <a
              href="/chi-siamo"
              data-testid="hero-cta-scopri"
              className="px-7 py-3.5 rounded-full border border-white/40 text-white text-sm font-semibold tracking-wide hover:bg-white hover:text-[#1c1f1a] transition-all"
            >
              Scopri la nostra storia
            </a>
          </div>
        </div>

        {/* Frosted Glass Search Panel */}
        <form
          onSubmit={submit}
          data-testid="hero-search-panel"
          className="relative mt-12 md:mt-16 max-w-5xl mx-auto glass-panel rounded-2xl md:rounded-full p-3 md:p-2 md:pl-4"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-1">
            <SearchDropdown
              testid="search-brand"
              label="Modello"
              value={brand}
              onChange={setBrand}
              options={[
                { value: "", label: "Seleziona" },
                ...brands.map((b) => ({ value: b, label: b })),
              ]}
            />
            <Divider />
            <SearchDropdown
              testid="search-fuel"
              label="Motorizzazione"
              value={fuel}
              onChange={setFuel}
              options={[
                { value: "", label: "Seleziona" },
                { value: "Elettrico", label: "Elettrico" },
                { value: "Benzina", label: "Benzina" },
                { value: "Diesel", label: "Diesel" },
                { value: "Ibrido", label: "Ibrido" },
              ]}
            />
            <Divider />
            <SearchDropdown
              testid="search-type"
              label="Tipologia"
              value={type}
              onChange={setType}
              options={[
                { value: "", label: "Tutti" },
                { value: "auto_usata", label: "Auto usata" },
                { value: "auto_nuova", label: "Auto nuova" },
                { value: "scooter", label: "Scooter" },
                { value: "moto", label: "Moto" },
              ]}
            />
            <button
              type="submit"
              data-testid="search-submit"
              className="mt-2 md:mt-0 md:ml-auto inline-flex items-center justify-center gap-2 px-7 md:px-10 py-3.5 rounded-full bg-[#5ab500] text-white text-sm font-bold tracking-wider uppercase hover:bg-[#4a9a00] hover:shadow-[0_8px_24px_rgba(90,181,0,0.5)] transition-all"
            >
              <Search size={14} /> Cerca
            </button>
          </div>
        </form>

        <div
          data-testid="hero-match-count"
          aria-hidden="true"
          className="hidden mt-5 text-center text-white text-sm tracking-[0.15em] font-semibold uppercase drop-shadow"
        >
          <span className="text-[#7CFC00] text-lg md:text-xl mr-1">{matchCount}</span>
          {" "}veicol{matchCount === 1 ? "o" : "i"} disponibil{matchCount === 1 ? "e" : "i"}
        </div>
      </div>
    </section>
  );
}

function SearchDropdown({ label, value, onChange, options, testid }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) || options[0],
    [options, value]
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Reset highlight when opened
  useEffect(() => {
    if (open) {
      const idx = options.findIndex((o) => o.value === value);
      setHighlight(idx >= 0 ? idx : 0);
    }
  }, [open, options, value]);

  const select = (v) => {
    onChange(v);
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(options.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = options[highlight];
      if (opt) select(opt.value);
    }
  };

  return (
    <div ref={wrapRef} className="relative flex-1 min-w-0 font-body">
      <button
        type="button"
        data-testid={testid}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((s) => !s)}
        onKeyDown={onKey}
        className="w-full text-left px-4 py-2 md:py-2.5 rounded-xl md:rounded-full hover:bg-black/[0.03] focus:bg-black/[0.04] outline-none transition"
      >
        <div className="text-[9px] uppercase tracking-[0.2em] text-[#7a7f77] font-bold mb-0.5">
          {label}
        </div>
        <div className="flex items-center gap-2">
          <span className="flex-1 text-sm font-semibold text-[#1f2937] truncate">
            {selected?.label}
          </span>
          <ChevronDown
            size={14}
            className={`text-[#7a7f77] transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {open && (
        <ul
          role="listbox"
          data-testid={`${testid}-menu`}
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 max-h-72 overflow-y-auto bg-white border border-black/[0.04] p-1.5 animate-fade-in"
          style={{
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.10)",
            fontFamily: "'Manrope','Outfit',system-ui,sans-serif",
          }}
        >
          {options.map((o, i) => {
            const isSelected = o.value === value;
            const isHighlighted = i === highlight;
            return (
              <li
                key={o.value || "__placeholder__"}
                role="option"
                aria-selected={isSelected}
                data-testid={`${testid}-option-${o.value || "any"}`}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  e.preventDefault(); // keep focus, avoid blur
                  select(o.value);
                }}
                className={`flex items-center justify-between gap-3 rounded-lg cursor-pointer select-none transition-colors duration-150 ${
                  isHighlighted ? "bg-[#f0fdf4]" : "bg-transparent"
                } ${isSelected ? "text-[#1f2937] font-semibold" : "text-[#1f2937] font-medium"}`}
                style={{ padding: "12px 15px" }}
              >
                <span className="text-sm truncate">{o.label}</span>
                {isSelected && (
                  <Check size={14} className="text-[#5ab500] flex-shrink-0" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Divider() {
  return <span className="hidden md:block w-px h-10 bg-black/10 self-center" />;
}
